<script setup lang="ts">
import { computed, inject, nextTick, provide, ref, watch } from "vue";

import { getFilterDefinitionList } from "~/common/filter";
import { getProperty, getPropertyWithConfigList } from "~/common/properties";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import GenericDropdownItem from "~/components/GenericDropdownItem.vue";
import MobileBottomSheet from "~/components/MobileBottomSheet.vue";
import { PARENT_ON_AFTER_CLOSE_FUNCTION, PARENT_ON_AFTER_OPEN_FUNCTION } from "~/constants/injection";
import { AI_PROPERTY_PSUEDO_DUID } from "~/constants/property";
import { FilterIcon, LockIcon, PlusIcon, TrashIcon, XIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import {
  ButtonSize,
  ButtonStyle,
  DropdownMenuItemKind,
  FilterApplicability,
  FilterConnector,
  IconSize,
  PropertyKind,
} from "~/shared/enums";
import type { Filter as IFilter } from "~/shared/types";
import { useAppStore } from "~/stores";
import { makeUuid } from "~/utils/common";
import { when } from "~/utils/wait";

import FilterArea from "./FilterArea.vue";

const props = defineProps<{
  selectedFilterDuid: string | null;
  singleFilter?: IFilter;
  custom?: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const appStore = useAppStore();

const parentOnAfterOpen = inject<() => void>(PARENT_ON_AFTER_OPEN_FUNCTION, () => {});
const parentOnAfterClose = inject<() => void>(PARENT_ON_AFTER_CLOSE_FUNCTION, () => {});

const show = ref(false);
const showAllFilters = ref(false);
const isHidden = ref(false);

const filterAreaRef = ref<InstanceType<typeof FilterArea> | null>(null);
const dropdownRef = ref<InstanceType<typeof DropdownMenu> | null>(null);

const filterDefinitions = getFilterDefinitionList(getPropertyWithConfigList());
const getFilterDefinition = (propertyDuid: string) => filterDefinitions.find((e) => e.propertyDuid === propertyDuid);

const selectedFilterDuid = computed(() => props.selectedFilterDuid);
const selectedFilterTitle = computed(() =>
  props.selectedFilterDuid ? (getFilterDefinition(props.selectedFilterDuid)?.title ?? "") : ""
);

const isNonDefaultFilter = (filter: IFilter) => {
  const defaultFilter = appStore.defaultFilters.find((e) => e.propertyDuid === filter.propertyDuid);
  if (!defaultFilter) {
    return true;
  }
  if (appStore.isFilterEqual(defaultFilter, filter)) {
    return false;
  }
  const defaultValues = new Set(defaultFilter.values ?? []);
  return ![...(filter.values ?? [])].some((value) => defaultValues.has(value));
};

const canRemoveFilter = (filter: IFilter) => {
  if (filter.locked) {
    return false;
  }
  return isNonDefaultFilter(filter);
};

const shouldShowRemoveButton = (filter: IFilter) => {
  const property = getProperty(filter.propertyDuid);
  if (property?.kind === PropertyKind.DEFAULT_DARTBOARD) {
    return false;
  }

  return isNonDefaultFilter(filter) || filter.locked;
};

const areAllFiltersDefault = computed(() =>
  appStore.filters.every((filter) => {
    const defaultFilter = appStore.defaultFilters.find((e) => e.propertyDuid === filter.propertyDuid);
    if (!defaultFilter) {
      return false;
    }

    const defaultValues = new Set(defaultFilter.values ?? []);
    const currentValues = filter.values ?? [];
    if (currentValues.length === 0 && defaultValues.size === 0) {
      return true;
    }
    if (currentValues.length === 0) {
      return false;
    }
    return currentValues.every((value) => defaultValues.has(value));
  })
);

const hasEmptyDefaultFilters = computed(() =>
  appStore.filters.some((filter) => {
    const defaultFilter = appStore.defaultFilters.find((e) => e.propertyDuid === filter.propertyDuid);
    return defaultFilter && (!filter.values || filter.values.length === 0);
  })
);

const shouldDisableButton = computed(
  () => (areAllFiltersDefault.value && !hasEmptyDefaultFilters.value) || appStore.filters.length === 0
);

const addFilter = (propertyDuid: string) => {
  const definition = filterDefinitions.find((e) => e.propertyDuid === propertyDuid);
  if (!definition) {
    return;
  }
  const newFilter: IFilter = {
    id: makeUuid(),
    propertyDuid,
    connector: FilterConnector.OR,
    applicability:
      definition.config.applicabilities.length > 0 ? definition.config.applicabilities[0] : FilterApplicability.IS,
    values: [],
  };
  appStore.setFilter(newFilter);

  // Set dartboard filter first
  const sortedFilters = computed(() => {
    const dartboardFilter = appStore.filters.find(
      (e) => getProperty(e.propertyDuid)?.kind === PropertyKind.DEFAULT_DARTBOARD
    );
    const otherFilters = appStore.filters.filter(
      (e) => getProperty(e.propertyDuid)?.kind !== PropertyKind.DEFAULT_DARTBOARD
    );
    return dartboardFilter ? [dartboardFilter, ...otherFilters] : otherFilters;
  });

  appStore.setAllFilters(sortedFilters.value);
  show.value = true;
  nextTick(() => {
    filterAreaRef.value?.addFilter(propertyDuid);
  });
  dropdownRef.value?.close();
};

const close = () => {
  show.value = false;
  showAllFilters.value = false;
  emit("close");
  parentOnAfterClose?.();
};

const resetFilters = () => {
  const defaultFiltersWithValues = appStore.defaultFilters.filter(
    (filter) => filter.values && filter.values.length > 0
  );
  appStore.setAllFilters(defaultFiltersWithValues);
};

provide(PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  isHidden.value = true;
});
provide(PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  isHidden.value = false;
});

watch([show, showAllFilters], (newValue, oldValue) => {
  const isOpen = newValue[0] || newValue[1];
  const wasOpen = oldValue[0] || oldValue[1];

  if (isOpen && !wasOpen) {
    parentOnAfterOpen?.();
  } else if (!isOpen && wasOpen) {
    appStore.filters
      .filter((filter) => !filter.values?.length && !filter.locked)
      .forEach((filter) => appStore.removeFilter(filter.propertyDuid));

    parentOnAfterClose?.();
  }
});

const sections = computed(() => {
  const filters = new Map(appStore.filters.map((e) => [e.propertyDuid, e]));
  const propertyDefinitions = getFilterDefinitionList(getPropertyWithConfigList());
  return [
    {
      title: "Fields",
      items: propertyDefinitions
        .map((definition) => {
          const filter = filters.get(definition.propertyDuid);
          if (!definition.config.available || filter) {
            return null;
          }
          return {
            title: definition.title,
            kind: DropdownMenuItemKind.COMPONENT,
            component: GenericDropdownItem,
            hidden: definition.propertyDuid === AI_PROPERTY_PSUEDO_DUID,
            componentArgs: {
              title: definition.title,
              icon: definition.icon,
              iconArgs: {
                class: `icon-sm ${
                  definition.propertyDuid === AI_PROPERTY_PSUEDO_DUID ? "text-recommendation-base" : ""
                }`,
              },
              onClick: async () => addFilter(definition.propertyDuid),
            },
          };
        })
        .filter(isDefined),
    },
  ];
});

watch(
  () => props.selectedFilterDuid,
  async (newValue) => {
    if (!newValue) {
      return;
    }
    show.value = true;
    parentOnAfterOpen?.();
    if (show.value) {
      await when(filterAreaRef);
      filterAreaRef.value?.addFilter(newValue);
    }
  },
  { immediate: true }
);

defineExpose({
  show,
  showAllFilters,
  addFilter,
});
</script>

<template>
  <MobileBottomSheet
    v-if="show || showAllFilters"
    :entity="show || showAllFilters"
    :hidden="isHidden"
    include-done
    should-update-keyboard-height
    title="Filters"
    class="flex flex-col gap-4 px-4"
    @close="close">
    <div class="flex flex-1 flex-col overflow-hidden">
      <div class="flex-1 overflow-y-auto">
        <div
          v-if="!showAllFilters && (props.singleFilter || selectedFilterDuid)"
          class="mt-4 flex w-full flex-row items-center gap-2 truncate rounded-[10px] p-3 bg-lt">
          <FilterArea
            ref="filterAreaRef"
            custom
            :selected-filter-title="selectedFilterTitle"
            :single-filter="props.singleFilter"
            :selected-filter-duid="selectedFilterDuid" />
        </div>

        <div v-if="showAllFilters" class="flex flex-col px-4 pt-4">
          <div
            v-if="!appStore.filters.length"
            class="flex w-full flex-col items-center gap-2 rounded-[10px] px-3 py-[38px] bg-lt">
            <FilterIcon class="size-10 shrink-0 text-lt" />
            <span class="text-base text-md">No filters</span>
          </div>

          <div v-else class="flex max-h-64 flex-col gap-3 pt-2">
            <div
              v-for="filter in appStore.filters"
              :key="filter.id"
              class="relative flex w-full flex-row items-center gap-2 rounded-[10px] p-3 bg-lt">
              <FilterArea custom :single-filter="filter" :selected-filter-duid="filter.propertyDuid" />
              <button
                v-if="shouldShowRemoveButton(filter)"
                type="button"
                class="absolute -right-1.5 -top-1.5 flex items-center justify-center rounded-full border bg-lt border-md icon-md"
                :class="!canRemoveFilter(filter) && 'pointer-events-none'"
                aria-label="Remove filter"
                @click="canRemoveFilter(filter) && appStore.removeFilter(filter.propertyDuid)"
                @keydown.enter="canRemoveFilter(filter) && appStore.removeFilter(filter.propertyDuid)">
                <component :is="filter.locked ? LockIcon : XIcon" class="size-full text-lt focus:outline-none" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex w-full max-w-full flex-row gap-3 px-4 pb-4 pt-3">
      <Button
        v-if="showAllFilters && appStore.filters.length"
        :btn-style="ButtonStyle.SECONDARY"
        :text="appStore.defaultFilters.length ? 'Reset' : 'Clear all'"
        :disabled="shouldDisableButton"
        borderless
        class="flex-1 basis-2/5 rounded-lg !p-3 bg-lt"
        :size="ButtonSize.CHIP"
        :icon="TrashIcon"
        :icon-size="IconSize.XS"
        @click="resetFilters" />

      <DropdownMenu
        ref="dropdownRef"
        :sections="sections"
        block
        show-search
        mobile-include-done
        header-title="Filter by"
        class="flex-1 basis-3/5">
        <Button
          :btn-style="ButtonStyle.SECONDARY"
          text="Add filter"
          borderless
          class="w-full rounded-lg !p-3 bg-lt"
          :text-style="!appStore.filters.length ? 'text-md' : 'text-primary-base'"
          :size="ButtonSize.CHIP"
          :icon="PlusIcon"
          :icon-args="{ class: !appStore.filters.length ? 'text-md' : 'text-primary-base' }"
          :icon-size="IconSize.XS" />
      </DropdownMenu>
    </div>
  </MobileBottomSheet>
</template>
